import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useLoaderData, useLocation, useNavigate } from "react-router-dom";
import "./CreatorInvite.scss";
import { CreatorData } from "../creator-profile/CreatorProfile";
import {
  Form,
  Input,
  InputNumber,
  Row,
  Col,
  Button,
  message as antdMessage,
  Modal,
  Select,
  DatePicker,
  TimePicker,
  Switch,
} from "antd";
import { CaretDownOutlined, PlusOutlined } from "@ant-design/icons";
import CollabSection from "pages/collab/CollabSection";
import { useSelector } from "react-redux";
import { BusinessState } from "redux-context/business";
import { UserState } from "redux-context/user";
import ModalVenue from "pages/collab/ModalVenue";
import VenueSelectionModal from "components/venue-map/VenueSelectionModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCopy,
  faLightbulb,
  faTrash,
  faVideo,
} from "@fortawesome/free-solid-svg-icons";
import {
  fetchAdditionalChargeCollabInvite,
  sendCollabInvite,
} from "services/SauceService";
import { formatUSD } from "helpers/money-format";
import { faTiktok } from "@fortawesome/free-brands-svg-icons";
import { CheckoutCharge } from "interfaces/checkout-charge";
import { SubscriptionType } from "enums/SubscriptionType";
import { usePaywall } from "components/paywall/paywallContext";
import { PaywallSource } from "components/paywall/Paywall";
import {
  trackCreatorInviteConfirmSendClicked,
  trackCreatorInvitePageViewed,
  trackCreatorInviteSendInvitationClicked,
} from "analytics";
import { getCreatorsListUrl } from "utils/creatorsUrl";
import IncentivizedReviewWarning from "components/incentivized-review-warning/IncentivizedReviewWarning";
import { detectIncentivizedReviewKeyword } from "helpers/incentivized-review-warning";

const { TextArea } = Input;

const CreatorInvite: React.FC = () => {
  const creatorData = useLoaderData() as CreatorData;
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { openPaywall } = usePaywall();
  const [deliverablesCollapsed, setDeliverablesCollapsed] =
    useState<boolean>(true);
  const [locationInput, setLocationInput] = useState<string>("");
  const [initialVenueSearch, setInitialVenueSearch] = useState<string>("");
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [showModalVenue, setShowModalVenue] = useState<boolean>(false);
  const [showVenueMapModal, setShowVenueMapModal] = useState<boolean>(false);
  const [venuesSelected, setVenuesSelected] = useState<{ uuid: string }[]>([]);
  // confirmação modal state
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [confirmPayload, setConfirmPayload] = useState<any>(null);
  const [checkoutInfo, setCheckoutInfo] = useState<CheckoutCharge>();
  const [inviteMessageWarningKeyword, setInviteMessageWarningKeyword] =
    useState<string | null>(null);
  const charLimit = 500;
  const { business, escrow, subscription } = useSelector(
    (state: BusinessState) => state.business,
  );
  const { loggedUser } = useSelector((state: UserState) => state.user);
  const freemium = subscription?.subscriptionType === SubscriptionType.Fremium;
  const hasTrackedPageViewedRef = useRef(false);

  const disabledPastDates = useCallback((current: any) => {
    if (!current) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return current.valueOf() < today.getTime();
  }, []);

  const deliverables = Form.useWatch("collabInstructions", form);

  const venues = useMemo(() => {
    return [
      ...(business?.venues?.map((item: any) => ({
        ...item,
        value: item.uuid,
        label: `${item.name} - ${item.address}`,
      })) || []),
    ];
  }, [business]);

  const deliverablesInfo = useMemo(() => {
    const map: Record<string, string> = {
      instagramReel: "Instagram Reel",
      instagramStory: "Instagram Story",
      tiktokVideo: "TikTok Video",
    };
    const parts: string[] = [];
    let total = 0;
    if (deliverables) {
      Object.keys(map).forEach((key) => {
        const arr = (deliverables as any)[key];
        if (Array.isArray(arr) && arr.length > 0) {
          parts.push(`${map[key]}`);
          total += 1;
        }
      });
    }
    return { total, text: parts.join(", ") };
  }, [deliverables]);

  const filteredVenues = useMemo(() => {
    if (!locationInput) return venues;
    return venues
      .filter((venue: any) =>
        venue.label.toLowerCase().includes(locationInput.toLowerCase()),
      )
      .sort((a: any, b: any) => {
        const aDisabled = !!a.disabled;
        const bDisabled = !!b.disabled;

        const diff = Number(aDisabled) - Number(bDisabled);
        if (diff !== 0) return diff;

        return a.label.localeCompare(b.label);
      });
  }, [locationInput, venues]);

  const getCompensationType = useCallback(
    (values?: any): string | null => {
      const compensation = values?.compensation ?? {};
      const cash = Number(compensation.cash || 0);
      const food = Number(compensation.food || 0);

      if (cash > 0 && food > 0) return "mixed";
      if (cash > 0) return "paid";
      if (food > 0) return "gifted";
      return null;
    },
    [],
  );

  const analyticsContext = useMemo(
    () => ({
      pathname: location.pathname,
      search: location.search,
      businessId: business?._id ?? null,
      userId: loggedUser?.id ?? null,
      userEmail: loggedUser?.email ?? null,
      planType: subscription?.planName ?? null,
      currency: subscription?.stripeDetails?.nextPaymentDetails?.currency ?? null,
    }),
    [
      business?._id,
      location.pathname,
      location.search,
      loggedUser?.email,
      loggedUser?.id,
      subscription?.planName,
      subscription?.stripeDetails?.nextPaymentDetails?.currency,
    ],
  );

  const getAdditionalCharges = useCallback(async () => {
    const cashComp = form.getFieldValue(["compensation", "cash"]) || 0;
    if (cashComp > 0) {
      try {
        const response: any = await fetchAdditionalChargeCollabInvite({
          businessId: business?._id,
          isPremium: creatorData?.foodie?.isPremium,
          cashComp,
        });
        setCheckoutInfo(response);
      } catch (error) {
        console.error("Error fetching additional charges:", error);
      }
    }
  }, [business?._id, creatorData?.foodie?.isPremium, form]);

  const onSaveVenue = useCallback(
    (venue: any) => {
      setVenuesSelected((prev) => [...prev, venue?.data?.name]);
      setShowModalVenue(false);
    },
    [setVenuesSelected],
  );

  const handleCustomLocation = (input: string) => {
    if (!input || venuesSelected.some((v) => v.uuid === input)) return;
    setInitialVenueSearch(input);
    setShowModalVenue(true);
  };

  const handleInviteMessageBlur = useCallback(
    (event: React.FocusEvent<HTMLTextAreaElement>) => {
      setInviteMessageWarningKeyword(
        detectIncentivizedReviewKeyword(event.target.value),
      );
    },
    [],
  );

  const handleRemoveLocation = (uuid: string) => {
    const newVenues = venuesSelected.filter((l) => l.uuid !== uuid);
    setVenuesSelected(newVenues);
    form.setFieldValue("venues", newVenues);
  };

  const handleClearSelection = () => {
    setVenuesSelected([]);
    form.setFieldValue("venues", []);
  };

  const handleLocationSelect = (value: string) => {
    const venue = venues.find((v: any) => v.value === value);
    let newVenues;
    if (venue) {
      console.log(venue);
      if (!venuesSelected.some((v) => v.uuid === venue.value)) {
        newVenues = [...venuesSelected, { ...venue }];
        setVenuesSelected(newVenues);
        form.setFieldValue("venues", newVenues);
        // handleValueChange();
      }
    } else {
      // handleCustomLocation(value);
    }
    setLocationInput("");
  };

  const addDeliverable = (type: string) => {
    const mapKey: Record<string, (string | number)[]> = {
      "instagram-reel": ["instagramReel"],
      "instagram-story": ["instagramStory"],
      "tiktok-video": ["tiktokVideo"],
    };

    const key = mapKey[type];
    const fieldPath = ["collabInstructions", ...key];
    const current = form.getFieldValue(fieldPath) || [];
    const newItem = {
      copyText: ["@mustard.love"],
      instruction: "",
    };
    form.setFieldsValue({
      collabInstructions: {
        ...(form.getFieldValue("collabInstructions") || {}),
        [key[0]]: [...current, newItem],
      },
    });

    setShowAddModal(false);
    setDeliverablesCollapsed(false);
  };

  const onSendInvite = async () => {
    const values = form.getFieldsValue();

    const preferredDates = (values.preferredDates || [])
      .map((entry: any) => {
        const dateMoment = entry?.date;
        const timeMoment = entry?.hour;
        if (!dateMoment || !timeMoment) return null;
        return {
          date: dateMoment.toISOString(),
          hour: timeMoment.format("hh:mm A"),
        };
      })
      .filter(Boolean);

    setConfirmPayload({ ...values, preferredDates });
    setShowConfirmModal(true);
  };

  const onFinish = async () => {
    await form.validateFields();
    const compCash = form.getFieldValue(["compensation", "cash"]);
    const compFood = form.getFieldValue(["compensation", "food"]);
    if ((!compCash || compCash <= 0) && (!compFood || compFood <= 0)) {
      form.setFields([
        {
          name: ["compensation", "food"],
          errors: ["At least one form of compensation is required."],
        },
        {
          name: ["compensation", "cash"],
          errors: ["At least one form of compensation is required."],
        },
      ]);
      return;
    }

    trackCreatorInviteSendInvitationClicked({
      ...analyticsContext,
      compensationType: getCompensationType(form.getFieldsValue(true)),
    });

    if (freemium) {
      openPaywall({
        source: PaywallSource.CreatorInvite,
        onSubscribe: onSendInvite,
      });
      return;
    }
    onSendInvite();
  };

  const handleConfirmSend = async () => {
    if (!confirmPayload) return;
    try {
      const values = confirmPayload;
      trackCreatorInviteConfirmSendClicked({
        ...analyticsContext,
        compensationType: getCompensationType(values),
      });
      const params = {
        businessId: business?._id,
        mustardUserId: creatorData.foodie.foodieId,
        ...values,
      };

      await sendCollabInvite(params);
      antdMessage.success("Invitation sent!");
      setShowConfirmModal(false);
      navigate(getCreatorsListUrl(business?.venues?.[0]?.addressDisplay ?? null));
    } catch (error) {
      console.error("Error sending invite:", error);
      antdMessage.error("Failed to send invitation.");
    }
  };

  const onSaveDraft = () => {
    const values = form.getFieldsValue();
    // future: persist draft
    antdMessage.success("Rascunho salvo");
    console.log("Draft values:", values);
  };

  useEffect(() => {
    if (showConfirmModal) {
      getAdditionalCharges();
    } else {
      setCheckoutInfo(undefined);
    }
  }, [showConfirmModal, getAdditionalCharges]);

  useEffect(() => {
    if (hasTrackedPageViewedRef.current) {
      return;
    }

    hasTrackedPageViewedRef.current = true;
    trackCreatorInvitePageViewed(analyticsContext);
  }, [analyticsContext]);

  return (
    <div className="creator-invite page-wrapper">
      <ModalVenue
        open={showModalVenue}
        onCancel={() => setShowModalVenue(false)}
        onSave={onSaveVenue}
        initialSearch={initialVenueSearch}
      />

      <VenueSelectionModal
        isOpen={showVenueMapModal}
        onClose={(selected) => {
          setShowVenueMapModal(false);
          if (selected && Array.isArray(selected)) {
            // Add new venues to the existing selection, avoiding duplicates
            const existingUuids = venuesSelected.map((v) => v.uuid);
            const newVenues = [
              ...venuesSelected,
              ...selected.filter((v) => !existingUuids.includes(v.uuid)),
            ];
            setVenuesSelected(newVenues);
            form.setFieldValue("venues", newVenues);
          }
        }}
        venues={venues.filter((v) => !v.disabled) as any}
        initialSelected={
          venuesSelected
            .map((v) => venues.find((venue: any) => venue.value === v.uuid))
            .filter(Boolean) as any
        }
      />
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          compensation: {
            cash: undefined,
            food: undefined,
            guestAllowed: true,
          },
          collabInstructions: {
            instagramReel: [
              {
                instruction: "Tag as a collaborator",
                copyText: ["@mustard.love"],
              },
            ],
            instagramStory: [
              {
                instruction: "Add mention",
                copyText: ["@mustard.love"],
              },
            ],
          },
        }}
      >
        <div className="invite-container">
          <div className="invite-card">
            <div className="creator-summary">
              <div className="avatar">
                <img
                  src={creatorData.foodie.avatarUrl || "/logo192.png"}
                  alt={creatorData.foodie.name}
                />
              </div>
              <div className="creator-info">
                <div className="name-row">
                  <h3 className="name">{creatorData.foodie.name}</h3>
                </div>
                <div className="handle">@{creatorData.foodie.username}</div>
                <div style={{ display: "flex", gap: 16, marginTop: 8 }}>
                  <div className="meta">
                    {creatorData.foodie.instagram?.followersCount ? (
                      <span>
                        {creatorData.foodie.instagram.followersCount.toLocaleString()}{" "}
                        followers
                      </span>
                    ) : null}
                  </div>
                  <div className="meta">
                    {creatorData.foodie.engagementRate ? (
                      <span>
                        {creatorData.foodie.engagementRate.toLocaleString()}{" "}
                        engagement
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="perf-right">
                <div className="perf-label">Performance Score</div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div className="perf-percent">
                    {creatorData?.foodie?.commitmentRate?.toFixed(0)}%
                  </div>
                  <div className="perf-progress" aria-hidden>
                    <div
                      className="perf-progress-fill"
                      style={{
                        width: `${creatorData?.foodie?.commitmentRate}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="invite-form">
              <div className="personal-message">
                <h4 style={{ marginBottom: 8 }}>Personal Message</h4>
                <Form.Item
                  name="inviteMessage"
                  label=""
                  className="no-margin"
                  rules={[
                    {
                      required: true,
                      message: "Please enter a personal message.",
                    },
                  ]}
                >
                  <TextArea
                    maxLength={charLimit}
                    showCount
                    placeholder={`Hi ${creatorData.foodie.name}! We've been following your amazing food content and would love to collaborate with you...`}
                    rows={6}
                    onBlur={handleInviteMessageBlur}
                    className={
                      inviteMessageWarningKeyword
                        ? "incentivized-review-warning-textarea"
                        : undefined
                    }
                  />
                </Form.Item>
                <IncentivizedReviewWarning
                  visible={Boolean(inviteMessageWarningKeyword)}
                />

                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    color: "#8c8c8c",
                    marginBottom: 12,
                  }}
                >
                  <FontAwesomeIcon icon={faLightbulb} color="#8c8c8c" />
                  Tip: Mention specific posts you liked or why you chose them
                </span>
              </div>

              <Row gutter={24} style={{ flexWrap: "nowrap" }}>
                <Col flex={1}>
                  <h4>Add Location</h4>
                  <span
                    className="extra-label"
                    style={{
                      fontSize: 16,
                      marginBottom: 10,
                      display: "block",
                      color: "#666666",
                      lineHeight: 1.3,
                    }}
                  >
                    Choose which locations creators can visit for this
                    collaboration
                  </span>
                  <Form.Item
                    name="venues"
                    rules={[
                      {
                        validator: (_, value) =>
                          value && value.length > 0
                            ? Promise.resolve()
                            : Promise.reject(
                              new Error(
                                "Please select at least one location",
                              ),
                            ),
                      },
                    ]}
                  >
                    <>
                      <Select
                        showSearch
                        value={locationInput}
                        placeholder="Search for brand locations..."
                        className="campaign-venues"
                        onSearch={setLocationInput}
                        onChange={handleLocationSelect}
                        notFoundContent={
                          locationInput &&
                            !filteredVenues.some(
                              (v: any) => v.label === locationInput,
                            ) ? (
                            <div style={{ padding: "8px", color: "#888" }}>
                              Press “Enter” to add a venue
                            </div>
                          ) : null
                        }
                        onInputKeyDown={(e) => {
                          if (e.key === "Enter" && locationInput) {
                            if (
                              !filteredVenues.some(
                                (v: any) => v.label === locationInput,
                              )
                            ) {
                              handleCustomLocation(locationInput);
                              setLocationInput("");
                            }
                          }
                        }}
                        filterOption={false}
                        options={filteredVenues}
                      />

                      <div style={{ display: "flex" }}>
                        <div className="campaign-tags" style={{ flex: 1 }}>
                          {venuesSelected.map(({ uuid }) => {
                            const venue = venues.find(
                              (v: any) => v.value === uuid,
                            );
                            return (
                              <div
                                key={uuid}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  background: "#f2f0de",
                                  borderRadius: 16,
                                  padding: "6px 12px",
                                  marginBottom: 4,
                                  fontSize: 14,
                                  height: 32,
                                  fontWeight: 500,
                                  width: 243,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                <span
                                  style={{
                                    flex: 1,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {venue ? venue.label : uuid}
                                </span>
                                <button
                                  onClick={() => handleRemoveLocation(uuid)}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    color: "#888",
                                    cursor: "pointer",
                                    marginLeft: 8,
                                    fontSize: 16,
                                    lineHeight: 1,
                                    padding: 0,
                                  }}
                                  aria-label="Remove"
                                  type="button"
                                >
                                  x
                                </button>
                              </div>
                            );
                          })}
                        </div>
                        {venuesSelected.length > 0 && (
                          <Col
                            style={{
                              display: "flex",
                            }}
                          >
                            <button
                              type="button"
                              onClick={handleClearSelection}
                              style={{
                                backgroundColor: "#fff",
                                color: "#666",
                                textDecoration: "underline",
                                border: "none",
                                cursor: "pointer",
                                fontSize: 14,
                                height: 32,
                              }}
                            >
                              Clear Selection
                            </button>
                          </Col>
                        )}
                      </div>
                    </>
                  </Form.Item>

                  {/* <VenueMap
                          venues={venues}
                          selectedVenueUuids={selectedVenueUuids}
                          onSelectionChange={handleMapSelectionChange}
                        /> */}
                </Col>
              </Row>

              <div className="compensation">
                <h4>Compensation Details</h4>
                <span
                  className="extra-label"
                  style={{
                    fontSize: 16,
                    marginBottom: 12,
                    display: "block",
                    color: "#666666",
                    lineHeight: 1.3,
                  }}
                >
                  What will {creatorData?.foodie?.name} receive for this
                  experience
                </span>
                <Row gutter={16}>
                  <Col flex={1}>
                    <div
                      style={{
                        marginBottom: 4,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 12,
                      }}
                    >
                      <h5 style={{ margin: 0 }}>Product</h5>
                      <span
                        style={{
                          color: "#666666",
                        }}
                      >
                        Recommended: $100
                      </span>
                    </div>
                    <Form.Item name={["compensation", "food"]} label="">
                      <InputNumber
                        style={{ width: "100%" }}
                        min={0}
                        formatter={(val) => (val ? `$ ${val}` : "")}
                        parser={(val: any) => val?.replace(/\$\s?|(,*)/g, "")}
                      />
                    </Form.Item>
                  </Col>

                  <Col flex={1}>
                    <div
                      style={{
                        marginBottom: 4,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 12,
                      }}
                    >
                      <h5 style={{ margin: 0 }}>Monetary</h5>
                      {creatorData?.foodie?.engagementCPMPriceMin &&
                        creatorData?.foodie?.engagementCPMPriceMin > 20 &&
                        creatorData?.foodie?.engagementCPMPriceMax &&
                        creatorData?.foodie?.engagementCPMPriceMax > 20 ? (
                        <span
                          style={{
                            color: "#666666",
                          }}
                        >
                          Recommended:{" "}
                          {`${formatUSD(
                            creatorData?.foodie?.engagementCPMPriceMax,
                          )}`}
                        </span>
                      ) : (
                        <span
                          style={{
                            color: "#666666",
                          }}
                        >
                          No Recommendation
                        </span>
                      )}
                    </div>
                    <Form.Item name={["compensation", "cash"]} label="">
                      <InputNumber
                        style={{ width: "100%" }}
                        min={0}
                        formatter={(val) => (val ? `$ ${val}` : "")}
                        parser={(val: any) => val?.replace(/\$\s?|(,*)/g, "")}
                      />
                    </Form.Item>
                  </Col>
                  <Col flex="170px">
                    <h5 style={{ margin: 0 }}>+1 Guest Allowed</h5>
                    <div
                      style={{
                        marginTop: 16,
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <Form.Item
                        name={["compensation", "guestAllowed"]}
                        label=""
                        valuePropName="checked"
                        noStyle
                      >
                        <Switch className="guest-switch" />
                      </Form.Item>
                    </div>
                  </Col>
                </Row>
              </div>

              <div className="compensation" style={{ marginTop: 32 }}>
                <h4>Content Deliverables</h4>
                <span
                  className="extra-label"
                  style={{
                    fontSize: 16,
                    marginBottom: 12,
                    display: "block",
                    color: "#666666",
                    lineHeight: 1.3,
                  }}
                >
                  Set specific instructions for each deliverable type to guide
                  creators on exactly what content to create and how to promote
                  your brand.
                </span>
                <div className="deliverables-panel">
                  <div
                    className="deliverables-header"
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      setDeliverablesCollapsed(!deliverablesCollapsed)
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ")
                        setDeliverablesCollapsed(!deliverablesCollapsed);
                    }}
                  >
                    <div className="left">
                      <div className="badge">
                        {deliverablesInfo.total || 0} Deliverable
                        {deliverablesInfo.total !== 1 ? "s" : ""}
                      </div>
                      <div className="summary">
                        {deliverablesInfo.text || "No deliverables selected"}
                      </div>
                    </div>
                    <div
                      className={`chevron ${deliverablesCollapsed ? "collapsed" : ""
                        }`}
                    >
                      <CaretDownOutlined />
                    </div>
                  </div>

                  <div
                    className={`deliverables-content ${deliverablesCollapsed ? "collapsed" : ""
                      }`}
                  >
                    <Row
                      style={{
                        flexDirection: "column",
                        marginBottom: 32,
                      }}
                    >
                      <CollabSection
                        type="instagram-reels"
                        namePrefix={["collabInstructions", "instagramReel"]}
                      />
                      <CollabSection
                        type="instagram-story"
                        namePrefix={["collabInstructions", "instagramStory"]}
                      />
                      <CollabSection
                        type="tiktok-video"
                        namePrefix={["collabInstructions", "tiktokVideo"]}
                      />

                      {/* Render generic misc deliverables added from modal */}
                      {/* <Form.List name={["deliverables", "misc"]}>
                        {(fields, { remove }) => (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 12,
                            }}
                          >
                            {fields.map(({ key, name, ...restField }) => (
                              <div
                                key={key}
                                style={{
                                  padding: 12,
                                  background: "#fff",
                                  borderRadius: 8,
                                  border: "1px solid #e6e6e6",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: 8,
                                  }}
                                >
                                  <strong>Custom Deliverable</strong>
                                  <Button
                                    type="text"
                                    danger
                                    onClick={() => remove(name)}
                                  >
                                    Remove
                                  </Button>
                                </div>
                                <Form.Item
                                  {...restField}
                                  name={[name, "instruction"]}
                                  rules={[
                                    { required: true, message: "Required" },
                                  ]}
                                >
                                  <Input placeholder="Instruction" />
                                </Form.Item>
                              </div>
                            ))}
                          </div>
                        )}
                      </Form.List> */}
                    </Row>
                    <Row>
                      <Button
                        type="dashed"
                        className="add-deliverable-btn"
                        icon={<PlusOutlined />}
                        block
                        onClick={() => setShowAddModal(true)}
                      >
                        Add Another Deliverable
                      </Button>
                    </Row>
                  </div>
                </div>
              </div>

              <div className="preferred-dates" style={{ marginTop: 32 }}>
                <h4>Preferred Visiting Dates</h4>
                <span
                  className="extra-label"
                  style={{
                    fontSize: 16,
                    marginBottom: 12,
                    display: "block",
                    color: "#666666",
                    lineHeight: 1.3,
                  }}
                >
                  Add a preferred date only if timing really matters
                </span>
                <Form.List name="preferredDates">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <div key={key} className="date-row">
                          <Form.Item
                            {...restField}
                            name={[name, "date"]}
                            noStyle
                            rules={[
                              {
                                required: true,
                                message: "Please select a date",
                              },
                            ]}
                          >
                            <DatePicker
                              disabledDate={disabledPastDates}
                              format="MM-DD-YYYY"
                            />
                          </Form.Item>

                          <Form.Item
                            {...restField}
                            name={[name, "hour"]}
                            noStyle
                            rules={[
                              {
                                required: true,
                                message: "Please select a time",
                              },
                            ]}
                          >
                            <TimePicker
                              use12Hours
                              format="hh:mm A"
                              needConfirm={false}
                            />
                          </Form.Item>

                          <Button type="text" onClick={() => remove(name)}>
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        </div>
                      ))}

                      <Button
                        onClick={() => add()}
                        icon={<PlusOutlined />}
                        className="add-btn"
                      >
                        Add Preferred Date
                      </Button>
                    </>
                  )}
                </Form.List>
              </div>

              <div className="actions">
                <Button
                  className="btn btn-ghost"
                  onClick={onSaveDraft}
                  type="default"
                  style={{ display: "none" }}
                >
                  Save Draft
                </Button>
                <Button
                  className="btn btn-primary"
                  type="primary"
                  htmlType="submit"
                >
                  Send Invitation
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Form>

      <Modal
        title="Add Deliverable"
        open={showAddModal}
        onCancel={() => setShowAddModal(false)}
        footer={null}
        centered
        width={450}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: 24,
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              width: "100%",
            }}
          >
            <Button
              block
              onClick={() => addDeliverable("instagram-reel")}
              style={{
                justifyContent: "flex-start",
                textAlign: "left",
                borderRadius: 16,
                padding: 16,
                height: "auto",
                gap: 12,
              }}
            >
              <div>
                <FontAwesomeIcon icon={faVideo} fontSize={24} color="#f6a623" />
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                <span style={{ fontWeight: 700, fontSize: 16 }}>
                  Instagram Reel
                </span>
                <div style={{ color: "#8c8c8c" }}>Short-form video content</div>
              </div>
            </Button>
            <Button
              block
              onClick={() => addDeliverable("instagram-story")}
              style={{
                justifyContent: "flex-start",
                textAlign: "left",
                borderRadius: 16,
                padding: 16,
                height: "auto",
                gap: 12,
              }}
            >
              <div>
                <FontAwesomeIcon icon={faCopy} fontSize={24} color="#f6a623" />
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                <span style={{ fontWeight: 700, fontSize: 16 }}>
                  Instagram Story
                </span>
                <div style={{ color: "#8c8c8c" }}>
                  24-hour temporary content
                </div>
              </div>
            </Button>
            <Button
              block
              onClick={() => addDeliverable("tiktok-video")}
              style={{
                justifyContent: "flex-start",
                textAlign: "left",
                borderRadius: 16,
                padding: 16,
                height: "auto",
                gap: 12,
              }}
            >
              <div>
                <FontAwesomeIcon
                  icon={faTiktok}
                  fontSize={24}
                  color="#f6a623"
                />
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                <span style={{ fontWeight: 700, fontSize: 16 }}>
                  TikTok Video
                </span>
                <div style={{ color: "#8c8c8c" }}>
                  Short-form vertical video
                </div>
              </div>
            </Button>
          </div>

          <div style={{ textAlign: "right" }}>
            <Button type="link" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      {/* Confirmation modal preview (matches screenshot/html) */}
      <Modal
        title="Confirm Invitation"
        open={showConfirmModal}
        onCancel={() => setShowConfirmModal(false)}
        footer={null}
        centered
        className="confirm-invite-modal"
        width={700}
      >
        <div className="confirm-modal-body">
          <div className="confirm-creator">
            <div className="confirm-avatar">
              <img
                src={creatorData.foodie.avatarUrl || "/logo192.png"}
                alt={creatorData.foodie.name}
              />
            </div>
            <div className="confirm-creator-info">
              <div className="confirm-creator-name">
                {creatorData.foodie.name}
              </div>
            </div>
          </div>

          <h4 className="confirm-section-title">Personal Message</h4>
          <div className="confirm-message">
            {confirmPayload?.inviteMessage || "No message provided"}
          </div>

          <h4 className="confirm-section-title">Locations selected</h4>
          <div className="confirm-locations-box">
            {confirmPayload?.venues && confirmPayload.venues.length > 0 ? (
              confirmPayload.venues.map((v: any, i: number) => (
                <div key={i} className="location-chip">
                  <span>{v.label}</span>
                </div>
              ))
            ) : (
              <div className="confirm-no-locations">No locations selected</div>
            )}
          </div>

          <h4 className="confirm-section-title">Offered Compensation</h4>
          <div className="confirm-comp-grid">
            {confirmPayload?.compensation?.food > 0 && (
              <div className="confirm-comp-box">
                <div className="confirm-comp-label">Product</div>
                <div className="confirm-comp-value">
                  ${confirmPayload?.compensation?.food ?? 0}
                </div>
              </div>
            )}

            {confirmPayload?.compensation?.cash > 0 && (
              <div className="confirm-comp-box">
                <div className="confirm-comp-label">Monetary</div>
                <div className="confirm-comp-value">
                  ${confirmPayload?.compensation?.cash ?? 0}
                </div>
              </div>
            )}
            {confirmPayload?.compensation?.guestAllowed && (
              <div className="confirm-comp-box">
                <div className="confirm-comp-label">Guest Allowed</div>
                <div className="confirm-comp-value">+1</div>
              </div>
            )}
          </div>

          <h4 className="confirm-section-title">
            Request Content Deliverables
          </h4>
          <div className="confirm-deliverables-box">
            <div className="confirm-deliverable-badge">
              {deliverablesInfo.total} Deliverable
              {deliverablesInfo.total !== 1 ? "s" : ""}
            </div>
            <div className="confirm-deliverable-text">
              {deliverablesInfo.text || "No deliverables selected"}
            </div>
          </div>

          <h4 className="confirm-section-title">Preferred Visiting Dates</h4>
          <div className="confirm-dates-box">
            {confirmPayload?.preferredDates &&
              confirmPayload.preferredDates.length > 0 ? (
              confirmPayload.preferredDates.map((d: any, i: number) => (
                <div key={i} className="confirm-dates-row">
                  {new Date(d.date).toLocaleDateString()}{" "}
                  {d.hour ? `• ${d.hour}` : ""}
                </div>
              ))
            ) : (
              <div className="confirm-no-dates">Regular Business Hours</div>
            )}
          </div>

          {(checkoutInfo?.totalCharge || 0) > 0 && (
            <>
              <ul className="payment-list" style={{ marginBottom: 16, gap: 0 }}>
                <li style={{ marginBottom: 0 }}>
                  <span>
                    Current escrow balance:{" "}
                    <b> {formatUSD(escrow?.availableAmount || 0)}</b>
                  </span>
                </li>

                {/* <li>
              <span>
                Spots available:{" "}
                <b> {spotsInformation?.amountRemaining || 0}</b>
              </span>
            </li> */}
              </ul>

              <ul className="payment-list">
                {(checkoutInfo?.influencerCashComp || 0) > 0 && (
                  <li>
                    <span>
                      <b> {formatUSD(checkoutInfo?.influencerCashComp || 0)}</b>{" "}
                      -- Influencer compensation
                    </span>
                  </li>
                )}

                {(checkoutInfo?.deducedFromEscrowBalanceAvailable || 0) > 0 && (
                  <li>
                    <span style={{ color: "green" }}>
                      <b>
                        -
                        {formatUSD(
                          checkoutInfo?.deducedFromEscrowBalanceAvailable || 0,
                        )}
                      </b>{" "}
                      -- Escrow balance
                    </span>
                  </li>
                )}

                {(checkoutInfo?.escrowFee || 0) > 0 && (
                  <li>
                    <span>
                      <b>{formatUSD(checkoutInfo?.escrowFee || 0)} </b>-- Escrow
                      fee
                    </span>
                  </li>
                )}

                {(checkoutInfo?.additionalSpot || 0) > 0 && (
                  <li>
                    <span>
                      <b> {formatUSD(checkoutInfo?.additionalSpot || 0)}</b> --
                      Spot credit
                    </span>
                  </li>
                )}
              </ul>

              <div className="total">
                <div className="total-amount">
                  <span>Total:</span>
                  <b>{formatUSD(checkoutInfo?.totalCharge || 0)}</b>
                </div>
                <div className="total-disclaimer">
                  <p>
                    You will only be charged after the creator accepts your
                    invite. The final charge will depend on your escrow balance
                    at that moment.
                  </p>
                </div>
              </div>
            </>
          )}

          <div className="confirm-actions">
            <Button onClick={() => setShowConfirmModal(false)} type="default">
              Cancel
            </Button>
            <Button onClick={handleConfirmSend} type="primary">
              {(checkoutInfo?.totalCharge || 0) > 0
                ? "Confirm & Send"
                : "Send Invitation"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CreatorInvite;
